import React, { useState } from "react";
import {
    Icon, IconButton, Input, InputGroup, InputLeftElement, InputRightElement, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdSearch, MdClose } from "react-icons/md";
import Card from "horizon/components/card/Card";
import PaymentsBrowseTable from "./PaymentsBrowseTable";
import usePaymentsBrowse from "./hooks/usePaymentsBrowse";

/**
 * Builds the contextual subtitle shown under the search bar.
 * @param {boolean} active - Whether a search term is active
 * @param {number} count - Number of items displayed
 * @returns {string}
 */
function buildSubtitle(active, count) {
    if (active) return `${count} résultat${count > 1 ? "s" : ""} de recherche`;
    return `${count} dernier${count > 1 ? "s" : ""} paiement${count > 1 ? "s" : ""}`;
}

/**
 * Renders the debounced search input plus a contextual label.
 * @param {Object} props - { value, onChange, active, count }
 * @returns {React.ReactElement}
 */
function SearchBar({ value, onChange, active, count }) {
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Stack spacing="8px" mb="16px">
            <InputGroup>
                <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.600" /></InputLeftElement>
                <Input value={value} onChange={(e) => onChange(e.target.value)} borderRadius="12px"
                    placeholder="Rechercher par email, référence, ville ou adresse..." />
                {value && (
                    <InputRightElement>
                        <IconButton aria-label="Effacer" size="sm" variant="ghost"
                            icon={<Icon as={MdClose} />} onClick={() => onChange("")} />
                    </InputRightElement>
                )}
            </InputGroup>
            <Text fontSize="0.72rem" color={subColor}>{buildSubtitle(active, count)}</Text>
        </Stack>
    );
}

/**
 * Browse panel for the "Paiements" tab. Combines a debounced search bar with the
 * browse table (10 results from /last or /search/<term>).
 * @returns {React.ReactElement}
 */
function PaymentsBrowsePanel() {
    const [query, setQuery] = useState("");
    const { items, loading, error, active } = usePaymentsBrowse(query);
    return (
        <Card>
            <SearchBar value={query} onChange={setQuery} active={active} count={items.length} />
            <PaymentsBrowseTable items={items} loading={loading} error={error} />
        </Card>
    );
}

export default PaymentsBrowsePanel;
